import { useEffect, useState } from "react";
import { Route as RouteIcon, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { AuthImage } from "@/components/AuthImage";
import { RouteDialog } from "@/components/RouteDialog";
import { api, DEPT } from "@/lib/api";

const T = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—");
const gmap = (lat, lng) => `https://www.google.com/maps?q=${lat},${lng}`;
const todayIST = () => new Date(Date.now() + 5.5 * 3600e3).toISOString().slice(0, 10);

const Punch = ({ time, selfie, lat, lng, color, onZoom, testid }) => (
  <div className="flex items-center gap-3" data-testid={testid}>
    {selfie ? (
      <button onClick={() => onZoom(selfie)} className="shrink-0"><AuthImage path={selfie} alt="selfie" className="h-12 w-12 rounded-lg object-cover border" /></button>
    ) : <div className="h-12 w-12 rounded-lg bg-slate-100 grid place-items-center text-[9px] text-slate-400 shrink-0">No selfie</div>}
    <div>
      <div className={`font-mono-num font-bold ${color}`}>{T(time)}</div>
      {lat != null && <a href={gmap(lat, lng)} target="_blank" rel="noreferrer" className="text-[11px] text-sky-700 inline-flex items-center gap-0.5"><MapPin className="h-3 w-3" />{lat.toFixed(4)}, {lng.toFixed(4)}</a>}
    </div>
  </div>
);

export default function AttendanceLog() {
  const [date, setDate] = useState(todayIST());
  const [rows, setRows] = useState([]);
  const [zoom, setZoom] = useState(null);
  const [route, setRoute] = useState(null);

  useEffect(() => {
    api.get("/attendance/log", { params: { date } }).then((r) => setRows(r.data));
  }, [date]);

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-4xl font-extrabold text-slate-900 tracking-tight">Login / Logout Log</h1>
          <p className="text-sm text-slate-500 mt-1">Selfie, time and GPS for every clock-in and clock-out, plus the day's location trail. HR &amp; Director only.</p>
        </div>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44 bg-white" data-testid="attendance-log-date-input" />
      </div>
      <div className="bg-white border rounded-2xl overflow-x-auto" data-testid="attendance-log-table">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead>Staff</TableHead><TableHead>Login</TableHead><TableHead>Logout</TableHead><TableHead className="text-right">Hours</TableHead><TableHead>Live Location</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-10">No clock-ins on this date.</TableCell></TableRow>}
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`attendance-log-row-${r.user_id}`}>
                <TableCell><div className="font-semibold">{r.full_name}</div><div className="text-xs text-slate-500">{DEPT[r.department]}</div></TableCell>
                <TableCell><Punch time={r.clock_in_time} selfie={r.clock_in_selfie_url} lat={r.clock_in_lat} lng={r.clock_in_lng} color="text-emerald-700" onZoom={setZoom} testid={`log-login-${r.user_id}`} /></TableCell>
                <TableCell>
                  {r.clock_out_time ? <Punch time={r.clock_out_time} selfie={r.clock_out_selfie_url} lat={r.clock_out_lat} lng={r.clock_out_lng} color="text-rose-600" onZoom={setZoom} testid={`log-logout-${r.user_id}`} />
                    : r.clock_in_time ? <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full">On duty</span> : "—"}
                </TableCell>
                <TableCell className="text-right font-mono-num">{r.total_hours_worked ?? "—"}</TableCell>
                <TableCell>
                  <Button size="sm" variant="outline" onClick={() => setRoute(r)} disabled={!r.ping_count && r.clock_in_lat == null} data-testid={`view-route-${r.user_id}`}>
                    <RouteIcon className="h-4 w-4 mr-1" />Trail ({r.ping_count})
                  </Button>
                  {r.last_ping && <div className="text-[10px] text-slate-500 mt-1">Last seen {T(r.last_ping)}</div>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Dialog open={!!zoom} onOpenChange={(o) => !o && setZoom(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="selfie-zoom-dialog">{zoom && <AuthImage path={zoom} alt="selfie" className="w-full rounded-xl" />}</DialogContent>
      </Dialog>
      <RouteDialog row={route} date={date} onClose={() => setRoute(null)} />
    </div>
  );
}
